import React, { useEffect, useState } from "react";
import { Button, Segmented, Space } from "antd";
import { CompressOutlined, EyeInvisibleOutlined, EyeOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import ScheduleDraftCompareSection from "./ScheduleDraftCompareSection";
import ScheduleDraftTimelineSection from "./ScheduleDraftTimelineSection";
import ScheduleDraftTimetableSection from "./ScheduleDraftTimetableSection";

const OBSERVATION_MODE_OPTIONS = [
    { label: "时间线", value: "timeline" },
    { label: "匹配审查", value: "compare" },
];

const TIMELINE_RANGE_MODE_OPTIONS = [
    { label: "整段", value: "full" },
    { label: "按周", value: "weekly" },
];

const ScheduleDraftObservationControls = ({
    observationMode,
    onObservationModeChange,
    onZoomIn,
    onZoomOut,
    onFit,
    showZoomControls,
    showTimelineCollapseToggle,
    showTimelineRangeControls,
    timelineCanvasCollapsed,
    onTimelineCanvasCollapsedChange,
    timelineRangeMode,
    onTimelineRangeModeChange,
    timelineRangeWeeklyDisabled,
}) => (
    <div className="schedule-draft-observation-controls">
        <div className="schedule-draft-observation-mode-controls">
            <Segmented size="middle" value={observationMode} onChange={onObservationModeChange} options={OBSERVATION_MODE_OPTIONS} className="schedule-draft-observation-mode-buttons schedule-draft-observation-mode-buttons-primary" aria-label="观察模式" />
            {showTimelineCollapseToggle || showTimelineRangeControls ? (
                <div className="schedule-draft-observation-secondary-controls">
                    {showTimelineCollapseToggle ? (
                        <Button
                            size="small"
                            className="schedule-draft-observation-secondary-button"
                            icon={timelineCanvasCollapsed ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                            onClick={() => onTimelineCanvasCollapsedChange?.(!timelineCanvasCollapsed)}
                        >
                            {timelineCanvasCollapsed ? "显示时间轴" : "隐藏时间轴"}
                        </Button>
                    ) : null}
                    {showTimelineRangeControls ? (
                        <Segmented
                            size="small"
                            value={timelineRangeMode}
                            onChange={onTimelineRangeModeChange}
                            options={TIMELINE_RANGE_MODE_OPTIONS.map((option) => ({
                                ...option,
                                disabled: option.value === "weekly" && timelineRangeWeeklyDisabled,
                            }))}
                            className="schedule-draft-observation-range-buttons schedule-draft-observation-pattern-buttons-secondary schedule-draft-timeline-range-buttons"
                            aria-label="时间线范围方式"
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
        {showZoomControls ? (
            <Space wrap size={[4, 4]} className="schedule-draft-observation-tool-controls">
                <Button size="small" icon={<ZoomInOutlined />} onClick={onZoomIn}>
                    放大
                </Button>
                <Button size="small" icon={<ZoomOutOutlined />} onClick={onZoomOut}>
                    缩小
                </Button>
                <Button size="small" icon={<CompressOutlined />} onClick={onFit}>
                    适配视图
                </Button>
            </Space>
        ) : null}
    </div>
);

const ScheduleDraftWorkspaceMainSection = ({ timelineRef, onZoomIn, onZoomOut, onFit, onObservationModeChange, onHoverTargetChange, onTrackAction }) => {
    const {
        data: { patternModel } = {},
        ui: { observationMode, setObservationMode, patternType, setPatternType, timelineCanvasCollapsed, setTimelineCanvasCollapsed },
    } = useScheduleDraftEditorWorkspace();
    const [timelineRangeMode, setTimelineRangeMode] = useState("full");
    const handleObservationModeChange = onObservationModeChange || setObservationMode;
    const effectiveObservationMode = observationMode === "pattern" ? "timeline" : observationMode;
    const isTimelineMode = effectiveObservationMode === "timeline";
    const hasTimelineSegments = Boolean(patternModel?.segments?.length);

    const handleTimelineCanvasCollapsedChange = (nextCollapsed) => {
        if (nextCollapsed) {
            timelineRef?.current?.clearPreview?.();
            onHoverTargetChange?.(null);
        }
        setTimelineCanvasCollapsed?.(Boolean(nextCollapsed));
    };

    const handleTimelineRangeModeChange = (nextMode) => {
        setTimelineRangeMode(nextMode);
        if (nextMode === "weekly" && patternType !== "weekly") {
            setPatternType?.("weekly");
        }
    };

    useEffect(() => {
        if (observationMode === "pattern") {
            handleObservationModeChange("timeline");
        }
    }, [handleObservationModeChange, observationMode]);

    useEffect(() => {
        if (timelineRangeMode === "weekly" && patternType === "weekly" && !hasTimelineSegments) {
            setTimelineRangeMode("full");
        }
    }, [hasTimelineSegments, patternType, timelineRangeMode]);

    const observationControls = (
        <ScheduleDraftObservationControls
            observationMode={effectiveObservationMode}
            onObservationModeChange={handleObservationModeChange}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
            onFit={onFit}
            showZoomControls={!isTimelineMode || !timelineCanvasCollapsed}
            showTimelineCollapseToggle={isTimelineMode}
            showTimelineRangeControls={isTimelineMode}
            timelineCanvasCollapsed={timelineCanvasCollapsed}
            onTimelineCanvasCollapsedChange={handleTimelineCanvasCollapsedChange}
            timelineRangeMode={timelineRangeMode}
            onTimelineRangeModeChange={handleTimelineRangeModeChange}
            timelineRangeWeeklyDisabled={patternType === "weekly" && !hasTimelineSegments}
        />
    );

    return (
        <div className="schedule-draft-main-column schedule-draft-workspace-main-column">
            {effectiveObservationMode === "compare" ? (
                <ScheduleDraftCompareSection timelineRef={timelineRef} observationControls={observationControls} onHoverTargetChange={onHoverTargetChange} />
            ) : (
                <>
                    <ScheduleDraftTimelineSection
                        timelineRef={timelineRef}
                        observationControls={observationControls}
                        onHoverTargetChange={onHoverTargetChange}
                        onTrackAction={onTrackAction}
                        timelineCanvasCollapsed={timelineCanvasCollapsed}
                        onTimelineCanvasCollapsedChange={handleTimelineCanvasCollapsedChange}
                        timelineRangeMode={timelineRangeMode}
                    />
                    <ScheduleDraftTimetableSection onTrackAction={onTrackAction} />
                </>
            )}
        </div>
    );
};

export default ScheduleDraftWorkspaceMainSection;
